/// <reference types="vite/client" />
import { api } from '@/lib/api';

// ─── Response Types ──────────────────────────────────────────────────────────

export interface HealthScore {
  total: number;
  grade: 'excellent' | 'good' | 'fair' | 'poor' | 'critical';
  breakdown: Record<string, { score: number; max: number }>;
  risk_factors: string[];
}

export interface BudgetPacing {
  status: string;
  monthly_budget?: number;
  spent_so_far?: number;
  expected_spend?: number;
  variance?: number;
  variance_pct?: number;
  recommendation?: string;
  message?: string;
}

// Fixed: backend returns roas_pct/cost/conv_value/status, not roas/revenue/period
export interface RoasData {
  roas_pct: number | null;
  cost: number;
  conv_value: number;
  profit?: number;
  target_roas_pct?: number | null;
  variance_from_target_pct?: number | null;
  status: string;
  recommendation: string;
  reason?: string;
}

export interface BiddingRecommendation {
  recommended_strategy: string;
  rationale: string;
  alternatives: string[];
  sources: string[];
}

// Fixed: backend returns { suggestions: [...], count } at root (no data wrapper)
// Each suggestion has: keyword, category, match_type_recommendation, intent, priority
export interface KeywordSuggestion {
  keyword: string;
  category: string;
  match_type_recommendation: string;
  intent: string;
  priority: number;
}

export interface KeywordDiscoverResult {
  suggestions: KeywordSuggestion[];
  count: number;
}

// Fixed: backend returns { suggestions: [...], count } at root (no data wrapper)
// Each suggestion is an ngram candidate with confidence/rationale
export interface NegativeSuggestion {
  ngram: string;
  recommended_match_type: string;
  recommended_scope: string;
  confidence: number;
  wasted_cost: number;
  wasted_clicks: number;
  occurrences: number;
  sample_terms: string[];
  rationale: string;
}

export interface NegativeSuggestResult {
  suggestions: NegativeSuggestion[];
  count: number;
}

// Fixed: backend returns raw object (no data wrapper), single title analysis
export interface FeedTitleAnalyzeResult {
  title: string;
  length?: number;
  score: number;
  grade: string;
  issues: string[];
  suggestions: string[];
}

// Fixed: backend returns raw object (no data wrapper), uses page_data input
export interface LandingPageAnalyzeResult {
  url?: string | null;
  total_score: number;
  grade: string;
  breakdown: Record<string, { score: number; max: number }>;
  issues: Array<{ severity: string; category: string; message: string }>;
  recommendations: string[];
}

// Fixed: backend returns raw object (no data wrapper)
// snapshot: { you, competitor_count, top_competitor, competitors, distribution }
export interface AuctionCompetitor {
  domain: string;
  impression_share: number;
  overlap_rate: number;
  outranking_share: number;
  classification: string;
}

export interface AuctionInsightsSnapshot {
  you?: Record<string, unknown> | null;
  competitor_count: number;
  top_competitor?: AuctionCompetitor | null;
  competitors: AuctionCompetitor[];
  distribution: Record<string, number>;
}

// compare returns different shape
export interface AuctionInsightsCompare {
  new_entrants: AuctionCompetitor[];
  departed: AuctionCompetitor[];
  rising: Array<AuctionCompetitor & { delta_share: number }>;
  declining: Array<AuctionCompetitor & { delta_share: number }>;
  stable: Array<AuctionCompetitor & { delta_share: number }>;
  alert_count: number;
}

export interface AnomalyDetectResult {
  anomalies: Array<{
    metric: string;
    z_score: number;
    severity: 'low' | 'medium' | 'high';
    direction: 'up' | 'down';
    description: string;
  }>;
  detected: boolean;
}

// DiagnosticsPriority: backend wraps in { data: { items, count } }
export interface DiagnosticsItem {
  id: number | string;
  rule_code?: string;
  message?: string;
  severity: string;
  status?: string;
  category?: string;
  entity_type?: string;
  score?: number;
}

export interface DiagnosticsPriority {
  items: DiagnosticsItem[];
  count: number;
}

// Fixed: backend returns raw object (no data wrapper)
// { total, average, bands: { great, good, average, below_average, poor }, health }
export interface QualityScoreDistribution {
  total: number;
  average: number;
  bands: {
    great: number;
    good: number;
    average: number;
    below_average: number;
    poor: number;
  };
  health: string;
}

// Fixed: backend returns raw object (no data wrapper)
// { valid, errors, warnings, ad_strength }
export interface RsaValidateResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
  ad_strength: string;
}

export interface ProfitabilityMetrics {
  revenue: number;
  cost: number;
  profit: number;
  margin: number;
  period: string;
  by_account: Array<{
    account_id: number;
    account_name: string;
    revenue: number;
    cost: number;
    profit: number;
    margin: number;
  }>;
}

export interface WorkloadMetrics {
  by_specialist: Array<{
    specialist_id: number;
    name: string;
    account_count: number;
    active_tasks: number;
    workload_score: number;
  }>;
}

export interface SlaMetrics {
  period_days: number;
  high_severity_findings_total: number;
  resolved_count: number;
  unresolved_count: number;
  avg_resolution_hours: number | null;
  resolution_rate_pct: number | null;
}

export interface ChurnRiskMetrics {
  accounts: Array<{
    account_id: number;
    account_name: string;
    risk_score: number;
    risk_level: 'low' | 'medium' | 'high' | 'critical';
    signals: string[];
  }>;
}

// Fixed: backend returns { categories: [...] } not { data: BrandSafetyLabel[] }
export interface BrandSafetyCategory {
  label: string;
  description: string;
  excluded_by_default: boolean;
}

export interface BrandSafetyLabelsResult {
  categories: BrandSafetyCategory[];
}

// Brand safety validate: raw { valid, errors, warnings, effective_exclusions }
export interface BrandSafetyValidateResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
  effective_exclusions: string[];
}

// Workflow evaluate: raw { trace, intents, terminated }
export interface WorkflowEvaluateResult {
  trace: Array<Record<string, unknown>>;
  intents: Array<Record<string, unknown>>;
  terminated: string;
}

// Conversion lag: raw { observed_total, corrected_total, uplift_pct, per_day }
export interface ConversionLagResult {
  observed_total: number;
  corrected_total: number;
  uplift_pct: number;
  per_day: Record<string, { observed: number; corrected: number; multiplier: number; days_ago: number; cumulative_fraction_visible?: number }>;
}

// Schedule parse: raw { slots, valid, errors, warnings, total_hours_per_week, slots_count }
export interface ScheduleSlot {
  day_of_week: string;
  day_short: string;
  start_hour: number;
  start_minute: string;
  end_hour: number;
  end_minute: string;
}

export interface ScheduleParseResult {
  slots: ScheduleSlot[];
  valid: boolean;
  errors: string[];
  warnings: string[];
  total_hours_per_week?: number;
  slots_count?: number;
}

// Locale format: raw { locale, supported_locales, currency_by_locale, examples }
export interface LocaleFormatResult {
  locale: string;
  supported_locales: string[];
  currency_by_locale: Record<string, string>;
  examples: {
    number: string;
    currency: string;
    percent: string;
    date_short: string;
    date_long: string;
  };
}

// Forecast: raw (no data wrapper)
export interface ForecastResult {
  account_id: number;
  horizon_days: number;
  projected_cost: number;
  projected_conversions: number;
  projected_conv_value: number;
  projected_roas_pct: number | null;
  projected_cpa: number | null;
  avg_daily_cost: number;
  avg_daily_conversions: number;
  seasonality_multiplier: number;
  days_observed: number;
  confidence: number;
  currency: string;
  method: string;
  disclaimer: string;
}

// Experiment analyze: raw { verdict, control, variant, relative_change_pct, p_value, ... }
export interface ExperimentAnalyzeResult {
  verdict: string;
  control: { impressions: number; conversions: number; rate: number };
  variant: { impressions: number; conversions: number; rate: number };
  relative_change_pct: number;
  p_value: number;
  significant: boolean;
  sample_size_ok: boolean;
  meaningful: boolean;
  recommended_action: string;
}

// ─── API Methods ──────────────────────────────────────────────────────────────

export const insightsApi = {
  healthScore: (accountId: number) =>
    api.get<{ data: HealthScore }>(`/accounts/${accountId}/health-score`),

  budgetPacing: (accountId: number) =>
    api.get<{ data: BudgetPacing }>(`/accounts/${accountId}/budget-pacing`),

  // Returns { data: RoasData } — wrapped
  roas: (accountId: number) =>
    api.get<{ data: RoasData }>(`/accounts/${accountId}/roas`),

  biddingRecommendation: (accountId: number) =>
    api.get<{ data: BiddingRecommendation }>(`/accounts/${accountId}/bidding-recommendation`),

  // Returns { data: { items, count } } — wrapped
  diagnosticsPriority: (accountId: number) =>
    api.get<{ data: DiagnosticsPriority }>(`/accounts/${accountId}/diagnostics-priority`),

  // Returns raw ForecastResult (no data wrapper)
  forecast: (accountId: number, body: Record<string, unknown>) =>
    api.post<ForecastResult>(`/accounts/${accountId}/forecast`, body),

  // Returns { suggestions, count } at root (no data wrapper), input is seed (string) not seeds array
  keywordDiscover: (accountId: number, body: { seed: string; locale?: string; brand_terms?: string[] }) =>
    api.post<KeywordDiscoverResult>(`/accounts/${accountId}/keyword-discover`, body),

  // Returns { suggestions, count } at root (no data wrapper), input needs search_terms with {term,clicks,conversions,cost}
  negativeSuggest: (accountId: number, body: { search_terms: Array<{ term: string; clicks: number; conversions: number; cost: number }> }) =>
    api.post<NegativeSuggestResult>(`/accounts/${accountId}/negative-suggest`, body),

  // Returns raw FeedTitleAnalyzeResult (no data wrapper), input is single title string
  feedTitleAnalyze: (accountId: number, body: { title: string; context?: Record<string, unknown> }) =>
    api.post<FeedTitleAnalyzeResult>(`/accounts/${accountId}/feed-title-analyze`, body),

  // Returns raw LandingPageAnalyzeResult (no data wrapper), input is page_data object
  landingPageAnalyze: (accountId: number, body: { page_data: Record<string, unknown>; target_keywords?: string[] }) =>
    api.post<LandingPageAnalyzeResult>(`/accounts/${accountId}/landing-page-analyze`, body),

  // Returns raw AuctionInsightsSnapshot (no data wrapper), input is rows array
  auctionInsightsSnapshot: (accountId: number, body: { rows: Array<{ domain: string; impression_share: number }> }) =>
    api.post<AuctionInsightsSnapshot>(`/accounts/${accountId}/auction-insights/snapshot`, body),

  // Returns raw AuctionInsightsCompare (no data wrapper)
  auctionInsightsCompare: (accountId: number, body: { current: Array<{ domain: string; impression_share: number }>; prior: Array<{ domain: string; impression_share: number }> }) =>
    api.post<AuctionInsightsCompare>(`/accounts/${accountId}/auction-insights/compare`, body),

  anomalyDetect: (accountId: number, body: Record<string, unknown>) =>
    api.post<{ data: AnomalyDetectResult }>(`/accounts/${accountId}/anomaly-detect`, body),

  // Returns raw QualityScoreDistribution (no data wrapper), POST with { scores: number[] }
  qualityScoreDistribution: (accountId: number, body: { scores: number[] }) =>
    api.post<QualityScoreDistribution>(`/accounts/${accountId}/quality-score/distribution`, body),

  // Returns raw RsaValidateResult (no data wrapper)
  rsaValidate: (accountId: number, body: { headlines: string[]; descriptions: string[]; paths?: string[] }) =>
    api.post<RsaValidateResult>(`/accounts/${accountId}/rsa-validate`, body),

  agencyProfitability: () =>
    api.get<{ data: ProfitabilityMetrics }>('/agency/metrics/profitability'),

  agencyWorkload: () =>
    api.get<{ data: WorkloadMetrics }>('/agency/metrics/workload'),

  agencySla: () =>
    api.get<{ data: SlaMetrics }>('/agency/metrics/sla'),

  agencyChurnRisk: () =>
    api.get<{ data: ChurnRiskMetrics }>('/agency/metrics/churn-risk'),

  // Returns { categories: [...] } (no data wrapper)
  brandSafetyLabels: () =>
    api.get<BrandSafetyLabelsResult>('/brand-safety/labels'),

  // Returns raw BrandSafetyValidateResult (no data wrapper)
  brandSafetyValidate: (body: { config: { brand_safety_opt_ins?: string[]; brand_safety_custom_exclusions?: string[] } }) =>
    api.post<BrandSafetyValidateResult>('/brand-safety/validate', body),

  // Returns raw WorkflowEvaluateResult (no data wrapper)
  workflowEvaluate: (body: { workflow: { steps: unknown[] }; context: Record<string, unknown> }) =>
    api.post<WorkflowEvaluateResult>('/workflow/evaluate', body),

  // Returns raw ConversionLagResult (no data wrapper), input is daily_conversions array
  conversionLagCorrect: (body: { daily_conversions: number[] }) =>
    api.post<ConversionLagResult>('/conversion/lag-correct', body),

  // Returns raw ScheduleParseResult (no data wrapper), input is compact string
  scheduleParse: (body: { compact: string }) =>
    api.post<ScheduleParseResult>('/schedule/parse', body),

  // Returns raw LocaleFormatResult (no data wrapper), GET with locale query param
  localeFormat: (locale: string) =>
    api.get<LocaleFormatResult>('/locale/format', { params: { locale } }),

  // Returns raw ExperimentAnalyzeResult (no data wrapper)
  experimentAnalyze: (experimentId: string | number, body: { control_data: { impressions: number; conversions: number }; variant_data: { impressions: number; conversions: number } }) =>
    api.post<ExperimentAnalyzeResult>(`/experiments/${experimentId}/analyze`, body),
};
